import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  generalSelector,
  checkLocationAvailability,
  clearLocationAvailability,
} from '../../../store/slices';
import { GetAddress } from './getAddress';
import { IAddress, ILocationAvailabilityPayload } from '../../../types';
import { PrimaryButton } from './buttons';

interface LocationAvailabilityCheckerProps {
  brandId?: number;
  branchId?: number;
  onAvailabilityCheck?: (isAvailable: boolean, message: string) => void;
}

export const LocationAvailabilityChecker: React.FC<
  LocationAvailabilityCheckerProps
> = ({ brandId, branchId, onAvailabilityCheck }) => {
  const dispatch = useAppDispatch();
  const { loading, locationAvailability } = useAppSelector(generalSelector);
  const [address, setAddress] = useState<IAddress>({
    address: '',
    area: '',
    latitude: 0,
    longitude: 0,
    placeId: '',
  });

  const handleAvailabilityCheck = async () => {
    if (
      !address.address ||
      !address.area ||
      !address.latitude ||
      !address.longitude
    ) {
      return;
    }

    if (!brandId && !branchId) {
      return;
    }

    const payload: ILocationAvailabilityPayload = {
      address: {
        latitude: address.latitude,
        longitude: address.longitude,
        area: address.area,
        placeId: address.placeId || '',
      },
      brandId,
      branchId,
    };

    try {
      await dispatch(checkLocationAvailability(payload)).unwrap();
      if (locationAvailability && onAvailabilityCheck) {
        onAvailabilityCheck(
          locationAvailability.available,
          locationAvailability.message
        );
      }
    } catch (error) {
      console.error('Failed to check availability:', error);
    }
  };

  React.useEffect(() => {
    // Clean up on unmount
    return () => {
      dispatch(clearLocationAvailability());
    };
  }, [dispatch]);

  return (
    <div className="mt-4">
      <h3 className="text-lg font-medium mb-4">Check Delivery Availability</h3>
      <GetAddress
        label="Enter Delivery Address"
        setAddress={setAddress}
        address={address}
      />
      <div className="mt-4">
        <PrimaryButton
          text="Check Availability"
          onClick={handleAvailabilityCheck}
          disabled={loading || !address.address || !address.area}
        />
      </div>

      {locationAvailability && (
        <div
          className={`mt-4 p-4 rounded ${
            locationAvailability.available ? 'bg-green-100' : 'bg-red-100'
          }`}
        >
          <p
            className={`font-medium ${
              locationAvailability.available ? 'text-green-700' : 'text-red-700'
            }`}
          >
            {locationAvailability.available
              ? 'Delivery Available!'
              : 'Delivery Not Available'}
          </p>
          <p className="mt-1 text-sm">{locationAvailability.message}</p>
        </div>
      )}
    </div>
  );
};
